import './SelectWorksite.scss';

import { Field, Form, Formik } from 'formik';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Transition } from 'react-transition-group';

import Button from '@/common/components/atoms/Button/Button';
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import SearchableSelect from '@/common/components/molecules/SearchableSelect/SearchableSelect';
import { ASC, ENDPOINT_WORKSITES, LABEL_KEY_PARAM } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import useResizeObserver from '@/common/hooks/useResizeObserver';
import useWorksites from '@/common/queries/worksites/useWorksites';
import { ReactComponent as PlusIcon } from '@/ebat/assets/icons/plus-circle.svg';
import WorksiteForm from '@/ebat/components/blocks/Account/Worksites/WorksiteForm/WorksiteForm';

import {
  SelectWorksiteDefaultProps,
  SelectWorksitePropTypes,
} from './SelectWorksite.proptypes';

const SelectWorksite = ({ disabled, entity, isLoading, onSelect, title }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { setModalClassName } = useModalContext();

  const nodeRef = useRef(null);

  const [showForm, setShowForm] = useState(false);

  const { data: worksites, isLoading: isLoadingWorksites } = useWorksites([
    ENDPOINT_WORKSITES,
    {
      order: LABEL_KEY_PARAM,
      direction: ASC,
      get_all: 1,
    },
  ]);

  const { height } = useResizeObserver(nodeRef);

  const transitionStyles = {
    entering: { height },
    entered: { height },
    exiting: { height: 0 },
    exited: { height: 0 },
  };

  const isDisabled = useMemo(() => {
    const loading = isLoading || isLoadingWorksites;
    const blocked = loading || disabled;

    return {
      loading,
      disabled: blocked,
      select: blocked || !user?.functionality_accesses?.worksites,
      manage: blocked || !user?.functionality_accesses?.worksites_manage,
    };
  }, [disabled, isLoading, isLoadingWorksites, user]);

  const handleShowForm = () => {
    setModalClassName('modal-select-worksite');
    setShowForm(true);
  };

  return (
    <div
      className="modal-select-worksite__content"
      data-qa-id="modal-select-worksite"
    >
      <div className="action-container">
        <div className="existing-worksite">
          <h2 className="rs-modal-title">
            {title || t('ebat:entities:worksites.select')}
          </h2>
          <Formik
            initialValues={{
              worksite_code: entity?.worksite?.code || '',
            }}
            onSubmit={async (values, actions) =>
              onSelect(values.worksite_code, actions)
            }
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form
                className="form modal-form"
                data-qa-id="select-worksite-form"
              >
                <Field
                  as={SearchableSelect}
                  className="existing-worksite__select"
                  context="worksite-code-field"
                  disabled={isDisabled.select}
                  errors={
                    errors.worksite_code &&
                    touched.worksite_code && [
                      t(
                        `common:messages:forms.validation.${errors.worksite_code}`,
                      ),
                    ]
                  }
                  id="worksite_code"
                  name="worksite_code"
                  data={
                    worksites?.data.map(({ code: value, label }) => ({
                      label,
                      value,
                    })) || []
                  }
                  placeholder={t('ebat:entities:worksites.forms.fields.select')}
                  required
                  onChange={(value) => setFieldValue('worksite_code', value)}
                >
                  {t('ebat:entities:worksites.forms.fields.existing')}
                </Field>
                <ButtonLoading
                  className="existing-worksite__submit"
                  context="select-worksite-save"
                  disabled={!values.worksite_code || isDisabled.select}
                  isLoading={isDisabled.loading}
                >
                  {t('common:messages:actions.valid')}
                </ButtonLoading>
                {!showForm && (
                  <Button
                    className="existing-worksite__toggle"
                    context="create-workste"
                    onClick={handleShowForm}
                    variant="simple-text"
                    withIcon
                  >
                    <PlusIcon className="button__icon" />
                    <span className="button__label">
                      {t(
                        'ebat:entities:common.modal.select_worksite.show_form',
                      )}
                    </span>
                  </Button>
                )}
              </Form>
            )}
          </Formik>
        </div>
        <Transition in={showForm} nodeRef={nodeRef} timeout={600}>
          {(state) => (
            <div
              style={{
                ...transitionStyles[state],
              }}
              className="new-worksite"
              data-qa-id="new-worksite"
            >
              <div
                className="new-worksite__container"
                ref={nodeRef}
                data-qa-id="new-worksite-container"
              >
                <h2 className="rs-modal-title">
                  {t('ebat:entities:worksites.forms.fields.create')}
                </h2>
                <div className="modal-form">
                  <WorksiteForm
                    disabled={isDisabled.manage}
                    isLoading={isDisabled.loading}
                    onSuccess={onSelect}
                  />
                </div>
              </div>
            </div>
          )}
        </Transition>
      </div>
    </div>
  );
};

SelectWorksite.defaultProps = {
  ...SelectWorksiteDefaultProps,
};

SelectWorksite.propTypes = {
  ...SelectWorksitePropTypes,
};

export default SelectWorksite;
